import { AppSidebar } from "@/components/nav_components/app-sidebar";
import Background from "@/components/ui/background";
import Header from "@/components/nav_components/header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Outlet } from "react-router-dom";
import type { ReactNode } from "react";
import { SignOutButton, useUser } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { AlertCircleIcon, LogOutIcon } from "lucide-react";
import { GlobalSearch } from "@/components/nav_components/global-search";
import { useComplianceRole } from "@/hooks/IsAdminContext";

export default function Dashboard() {
  // Compliance reviewers: no ⌘K search (it surfaces campaign/creator routes)
  // and no mailbox-connect dialog (they never send email).
  const { isCompliance } = useComplianceRole();
  return (
    <AppShell sidebar={<AppSidebar />}>
      {!isCompliance && <ConnectEmailAccount />}
      {!isCompliance && <GlobalSearch />}
    </AppShell>
  );
}

/**
 * The app frame (sidebar, header, page card) shared by /dashboard and the
 * staff-only /manager area — each passes its own sidebar.
 */
export function AppShell({ sidebar, children }: { sidebar: ReactNode; children?: ReactNode }) {
  return (
    <div className="w-full h-screen flex items-center justify-center">
      <SidebarProvider>
        {sidebar}
        <SidebarInset className="h-svh overflow-hidden">
          <Header />
          {/* Full radius (lg+): the header sits on the tinted frame and the page
              below it becomes a rounded card, 8px from the window and from the
              sidebar's items. The background pattern lives inside the card —
              `isolate` keeps its -z-10 above the card's own surface. */}
          <div className="relative isolate flex-1 min-h-0 overflow-hidden lg:radius-full:mr-2 lg:radius-full:mb-2 lg:radius-full:rounded-xl lg:radius-full:border lg:radius-full:shadow-soft">
            <Background />
            <div className="h-full w-full overflow-auto">
              <Outlet />
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
      {children}
    </div>
  );
}


import { useReverification } from "@clerk/react";

function ConnectEmailAccount() {
  const { user } = useUser();

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    if (!user) {
      toast.error("You must be logged in to connect an email account.");
      return;
    }

    const strategy = provider === 'google' ? 'oauth_google' : 'oauth_microsoft';

    const response = await user.createExternalAccount({
      strategy,
      redirectUrl: `${window.location.origin}${location.pathname}`,
    });

    if (response?.verification?.externalVerificationRedirectURL) {
      window.location.href = response.verification.externalVerificationRedirectURL.href;
    }
  };

  const disconnectAccount = useReverification(async () => {
    if (!user?.externalAccounts?.[0]) return;
    await user.externalAccounts[0].destroy();
  });

  const handleDisconnect = async () => {
    try {
      await disconnectAccount();
      toast.success("Email account disconnected");
    } catch (error) {
      // if (isClerkRuntimeError(error) && isReverificationCancelledError(error)) {
      //   toast.error("Reverification cancelled");
      // } else {
      //   toast.error("Failed to disconnect account");
      // }
    }
  };

  const hasNoExternalAccounts = user?.externalAccounts?.length === 0;
  const externalAccount = user?.externalAccounts?.[0];
  const externalAccountExpired = externalAccount?.verification?.status === "expired";
  const externalAccountHasError = !!externalAccount?.verification?.error;

  const requireOauth = user?.publicMetadata?.require_oauth === true;
  const shouldShowDialog = hasNoExternalAccounts || externalAccountHasError || externalAccountExpired;

  return (
    <>
      {(shouldShowDialog && requireOauth) && (
        <AlertDialog defaultOpen={true} open={true}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Connect Your Email Account
              </AlertDialogTitle>
              <AlertDialogDescription>
                Amy needs access to your email to send emails to creators from your inbox.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Alert variant="destructive">
              <AlertTitle>
                <AlertCircleIcon />
                {externalAccountHasError && externalAccount?.verification?.error?.message}
                {hasNoExternalAccounts && "Give Amy access to your email to get started."}
                {externalAccountExpired && "Your email account has expired."}
              </AlertTitle>
              <AlertDescription>
                {externalAccountHasError && externalAccount?.verification?.error?.longMessage}
                {hasNoExternalAccounts && "Amy needs access to your email to send emails to creators from your inbox."}
                {externalAccountExpired && "Since your email account has expired, you will need to connect it again."}
              </AlertDescription>
            </Alert>

            <div className="flex flex-col gap-2">
              {hasNoExternalAccounts ? (
                <>
                  <Button
                    onClick={() => handleConnect('google')}
                    className="w-full"
                    variant="neutral"
                  >
                    <img src="/google.svg" className="size-4" alt="Google Logo" />
                    Connect Gmail
                  </Button>

                  <Button
                    onClick={() => handleConnect('microsoft')}
                    className="w-full"
                    variant="neutral"
                  >
                    <img src="/microsoft.svg" className="size-4" alt="Microsoft Logo" />
                    Connect Outlook
                  </Button>
                </>
              ) : externalAccount ? (
                <>
                  <Button
                    onClick={() => handleConnect(externalAccount.provider === 'google' ? 'google' : 'microsoft')}
                    className="w-full"
                    variant="neutral"
                  >
                    <img
                      src={externalAccount.provider === 'google' ? '/google.svg' : '/microsoft.svg'}
                      className="size-4"
                      alt={`${externalAccount.provider} Logo`}
                    />
                    Reconnect {externalAccount.provider === 'google' ? 'Gmail' : 'Outlook'}
                  </Button>

                  <Button
                    onClick={handleDisconnect}
                    className="w-full"
                    variant="destructive"
                  >
                    Remove Account
                  </Button>
                </>
              ) : null}
            </div>

            <SignOutButton>
              <Button variant="outline" className="w-full">
                <LogOutIcon /> Log Out
              </Button>
            </SignOutButton>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}